import type { FeatureLike } from "ol/Feature";
import { Circle, Fill, Stroke, Style } from "ol/style";
import type { InrixVolumesViewOrderItemSummary } from "../../../../../../../types/ViewOrderItem/InrixVolumes/InrixVolumesViewOrderItemSummary";
import { InrixVolumeTimePeriod } from "../../controls";
import { parseInrixVolumeFeature } from "../../models/InrixVolumeFeature";
import { getInrixVolumeFeatureValue } from "../../utils/getInrixVolumeFeatureValue";
import {
  DEFAULT_STROKE_WIDTH,
  calculateSqrtScaledStrokeWidth,
  INRIX_VOLUME_LINE_COLOR,
} from "../../utils/inrixVolumeLineStyle";

const createViewOrderItemStyle = (strokeWidth: number) =>
  new Style({
    stroke: new Stroke({
      color: INRIX_VOLUME_LINE_COLOR,
      width: strokeWidth,
      lineCap: "round",
      lineJoin: "round",
    }),
    fill: new Fill({
      color: "rgba(34, 139, 230, 0.25)",
    }),
    image: new Circle({
      radius: 4,
      fill: new Fill({
        color: "rgba(34, 139, 230, 0.9)",
      }),
      stroke: new Stroke({
        color: "#ffffff",
        width: 1,
      }),
    }),
  });

interface CreateStyleFunctionArgs {
  summary: InrixVolumesViewOrderItemSummary | null;
  timePeriod: InrixVolumeTimePeriod;
  timeIndex: number | null;
  timeSegment: number | null;
}

const createStyleFunction = ({
  summary,
  timePeriod,
  timeIndex,
  timeSegment,
}: CreateStyleFunctionArgs) => {
  const styleCache = new Map<number, Style>();

  return (feature: FeatureLike) => {
    let strokeWidth = DEFAULT_STROKE_WIDTH;
    const model = parseInrixVolumeFeature(feature);

    if (model !== null) {
      if (timePeriod === InrixVolumeTimePeriod.ADT) {
        const adt = getInrixVolumeFeatureValue(model, timePeriod, timeIndex);
        strokeWidth = calculateSqrtScaledStrokeWidth(
          adt,
          summary?.adtMin,
          summary?.adtMax,
        );
      } else if (timeSegment !== null) {
        const volume = getInrixVolumeFeatureValue(model, timePeriod, timeIndex);
        strokeWidth = calculateSqrtScaledStrokeWidth(
          volume,
          summary?.volMin,
          summary?.volMax,
        );
      }
    }

    const cacheKey = Math.round(strokeWidth * 100) / 100;
    const existingStyle = styleCache.get(cacheKey);
    if (existingStyle) {
      return existingStyle;
    }

    const nextStyle = createViewOrderItemStyle(cacheKey);
    styleCache.set(cacheKey, nextStyle);
    return nextStyle;
  };
};

export default createStyleFunction;
